import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchMyProfile,
  type MyProfile,
  type UserPreferences,
  updateMyPreferences,
} from '@/lib/api/profile.js';
import { useAuthStore } from '@/stores/auth.js';
import { applyTheme, watchSystemTheme, type ThemePreference } from '@/lib/theme.js';

type UserPreferencesContextValue = {
  profile: MyProfile | null;
  preferences: UserPreferences | null;
  isLoading: boolean;
  refreshProfile: () => Promise<void>;
  setTheme: (theme: ThemePreference) => Promise<void>;
  applyPreferencesLocally: (prefs: Partial<UserPreferences>) => void;
};

const UserPreferencesContext = createContext<UserPreferencesContextValue | null>(null);

const DEFAULT_PREFS: UserPreferences = {
  theme: 'light',
  defaultTicketView: 'cards',
  defaultQueueFilter: 'all_active',
  defaultReportPeriod: 'month',
  defaultIntakeType: 'walk_in',
  autoAssignToMe: false,
  promisedTimeOffset: 'calculator',
  language: 'en',
};

function syncQueuePrefs(prefs: UserPreferences): void {
  if (prefs.defaultTicketView) {
    localStorage.setItem('fixflow_ticket_view', prefs.defaultTicketView);
  }
  if (prefs.defaultQueueFilter === 'my_tickets') {
    localStorage.setItem('fixflow_queue_my_tickets', 'true');
    localStorage.removeItem('fixflow_queue_filter_pref');
  } else if (prefs.defaultQueueFilter === 'unassigned') {
    localStorage.setItem('fixflow_queue_filter_pref', 'unassigned');
    localStorage.removeItem('fixflow_queue_my_tickets');
  } else {
    localStorage.removeItem('fixflow_queue_my_tickets');
    localStorage.removeItem('fixflow_queue_filter_pref');
  }
}

export function UserPreferencesProvider({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const updateSessionUser = useAuthStore((s) => s.updateUser);
  const queryClient = useQueryClient();
  const [localPrefs, setLocalPrefs] = useState<UserPreferences | null>(null);

  const profileQuery = useQuery({
    queryKey: ['users-me'],
    queryFn: fetchMyProfile,
    enabled: isAuthenticated,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!profileQuery.data) return;
    const prefs = { ...DEFAULT_PREFS, ...profileQuery.data.preferences };
    setLocalPrefs(prefs);
    applyTheme(prefs.theme);
    syncQueuePrefs(prefs);
    updateSessionUser({
      firstName: profileQuery.data.firstName,
      lastName: profileQuery.data.lastName,
      email: profileQuery.data.email,
    });
    return watchSystemTheme(prefs.theme);
  }, [profileQuery.data, updateSessionUser]);

  const refreshProfile = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['users-me'] });
  }, [queryClient]);

  const setTheme = useCallback(
    async (theme: ThemePreference) => {
      applyTheme(theme);
      setLocalPrefs((prev) => ({ ...(prev ?? DEFAULT_PREFS), theme }));
      try {
        const next = await updateMyPreferences({ theme });
        setLocalPrefs({ ...DEFAULT_PREFS, ...next });
        await queryClient.invalidateQueries({ queryKey: ['users-me'] });
      } catch {
        /* theme already applied locally */
      }
    },
    [queryClient],
  );

  const applyPreferencesLocally = useCallback((prefs: Partial<UserPreferences>) => {
    setLocalPrefs((prev) => {
      const next = { ...(prev ?? DEFAULT_PREFS), ...prefs };
      syncQueuePrefs(next);
      return next;
    });
    if (prefs.theme) applyTheme(prefs.theme);
  }, []);

  const value = useMemo<UserPreferencesContextValue>(
    () => ({
      profile: profileQuery.data ?? null,
      preferences: localPrefs ?? profileQuery.data?.preferences ?? null,
      isLoading: profileQuery.isLoading,
      refreshProfile,
      setTheme,
      applyPreferencesLocally,
    }),
    [
      profileQuery.data,
      profileQuery.isLoading,
      localPrefs,
      refreshProfile,
      setTheme,
      applyPreferencesLocally,
    ],
  );

  return (
    <UserPreferencesContext.Provider value={value}>{children}</UserPreferencesContext.Provider>
  );
}

export function useUserPreferences(): UserPreferencesContextValue {
  const ctx = useContext(UserPreferencesContext);
  if (!ctx) {
    return {
      profile: null,
      preferences: null,
      isLoading: false,
      refreshProfile: async () => undefined,
      setTheme: async (theme) => {
        applyTheme(theme);
      },
      applyPreferencesLocally: () => undefined,
    };
  }
  return ctx;
}
